// src/Tenant-Admin/TimeTable/components/common/ExcelBulkUpload.jsx

import React, { useState, useRef } from 'react';
import { 
  FaDownload, 
  FaUpload, 
  FaSpinner, 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaEye,
  FaFileExcel,
  FaTrashAlt,
  FaArrowRight,
  FaCloudUploadAlt
} from 'react-icons/fa';
import api from '../../../../api';

const ExcelBulkUpload = ({ 
  tenantId, 
  academicYearId, 
  semesterType, 
  termId, 
  selectedClassIds,
  onUploadComplete 
}) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // ============================================================
  // Download Template
  // ============================================================
  const handleDownloadTemplate = async () => {
    if (!tenantId || !academicYearId || !selectedClassIds || selectedClassIds.length === 0) {
      alert('Please select at least one class before downloading template');
      return;
    }

    setLoading(true);
    try {
      const classIdsParam = selectedClassIds.join(',');
      
      let url = `/api/timetable/export-template?tenantId=${tenantId}&academicYearId=${academicYearId}&classIds=${classIdsParam}`;
      
      if (semesterType && semesterType !== 'null' && semesterType !== 'undefined') {
        url += `&semesterType=${semesterType}`;
      }
      if (termId && termId !== 'null' && termId !== 'undefined') {
        url += `&termId=${termId}`;
      }

      const response = await api.get(url, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `Timetable_Template_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

    } catch (error) {
      console.error('Download template error:', error);
      alert('Failed to download template. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // File Management
  // ============================================================
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (ext !== 'xlsx' && ext !== 'xls') {
        alert('Please upload only .xlsx or .xls files');
        event.target.value = '';
        return;
      }
      setSelectedFile(file);
      setUploadResult(null);
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = () => {
    handleClearFile();
  };

  // ============================================================
  // Drag and Drop
  // ============================================================
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const ext = file.name.split('.').pop().toLowerCase();
      if (ext !== 'xlsx' && ext !== 'xls') {
        alert('Please upload only .xlsx or .xls files');
        return;
      }
      setSelectedFile(file);
      setUploadResult(null);
    }
  };

  // ============================================================
  // Upload & Validate
  // ============================================================
  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('tenantId', tenantId);
    formData.append('academicYearId', academicYearId);
    if (semesterType && semesterType !== 'null' && semesterType !== 'undefined') {
      formData.append('semesterType', semesterType);
    }
    if (termId && termId !== 'null' && termId !== 'undefined') {
      formData.append('termId', termId);
    }

    try {
      const response = await api.post('/api/timetable/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      setUploadResult(response.data);
      
      if (response.data.success && onUploadComplete) {
        onUploadComplete(response.data);
      }

    } catch (error) {
      console.error('Upload error:', error);
      setUploadResult({
        success: false,
        message: error.response?.data?.message || 'Upload failed. Please try again.',
        errors: error.response?.data?.errors || []
      });
    } finally {
      setUploading(false);
    }
  };

  // ============================================================
  // Preview
  // ============================================================
  const handlePreview = () => {
    if (uploadResult && onUploadComplete) {
      onUploadComplete(uploadResult);
    }
  };

  // ============================================================
  // Format File Size
  // ============================================================
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // ============================================================
  // Render
  // ============================================================
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <FaFileExcel className="text-indigo-600 text-xl" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Excel Bulk Upload</h3>
              <p className="text-sm text-gray-500">
                Upload timetables for multiple classes at once
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
              {selectedClassIds?.length || 0} class(es) selected
            </span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ============================================================
              STEP 1: Download Template
              ============================================================ */}
          <div className="border border-gray-200 rounded-lg p-5 bg-gray-50/50 hover:bg-gray-50 transition-colors">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <span className="text-indigo-600 font-bold text-sm">1</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-700">Download Template</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Get the Excel template with pre-filled dropdowns
                </p>
                <div className="mt-3 flex items-center gap-3">
                  <button
                    onClick={handleDownloadTemplate}
                    disabled={loading || !selectedClassIds?.length}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 text-sm font-medium shadow-sm hover:shadow"
                  >
                    {loading ? (
                      <FaSpinner className="animate-spin" />
                    ) : (
                      <FaDownload />
                    )}
                    Download Template
                  </button>
                  <span className="text-xs text-gray-400">
                    {selectedClassIds?.length || 0} classes
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ============================================================
              STEP 2: Upload & Validate
              ============================================================ */}
          <div className="border border-gray-200 rounded-lg p-5 bg-gray-50/50 hover:bg-gray-50 transition-colors">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <span className="text-green-600 font-bold text-sm">2</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-700">Upload & Validate</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Upload your filled Excel file for validation
                </p>
                
                {/* Drag & Drop Area */}
                <div
                  className={`mt-3 border-2 border-dashed rounded-lg p-4 text-center transition-all ${
                    isDragging 
                      ? 'border-indigo-500 bg-indigo-50' 
                      : selectedFile 
                        ? 'border-green-400 bg-green-50' 
                        : 'border-gray-300 hover:border-indigo-300'
                  }`}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  {selectedFile ? (
                    // File Selected State
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <FaFileExcel className="text-green-600 text-2xl flex-shrink-0" />
                        <div className="flex-1 min-w-0 text-left">
                          <p className="text-sm font-medium text-gray-700 truncate">
                            {selectedFile.name}
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatFileSize(selectedFile.size)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleRemoveFile}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                        title="Remove file"
                      >
                        <FaTrashAlt size={14} />
                      </button>
                    </div>
                  ) : (
                    // No File State
                    <div className="flex flex-col items-center gap-1 py-2">
                      <FaCloudUploadAlt className="text-gray-400 text-2xl" />
                      <p className="text-sm text-gray-500">
                        Drop your Excel file here or{' '}
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="text-indigo-600 hover:text-indigo-700 font-medium"
                        >
                          browse
                        </button>
                      </p>
                      <p className="text-xs text-gray-400">
                        Supports .xlsx and .xls files
                      </p>
                    </div>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={uploading}
                  />
                </div>

                {/* Upload Button */}
                {selectedFile && (
                  <div className="mt-3 flex items-center gap-3">
                    <button
                      onClick={handleUpload}
                      disabled={uploading}
                      className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 text-sm font-medium shadow-sm hover:shadow"
                    >
                      {uploading ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          Validating...
                        </>
                      ) : (
                        <>
                          <FaUpload />
                          Upload & Validate
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleClearFile}
                      className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================
            Upload Result
            ============================================================ */}
        {uploadResult && (
          <div className={`mt-6 rounded-lg border p-5 transition-all ${
            uploadResult.success 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-0.5">
                {uploadResult.success ? (
                  <FaCheckCircle className="text-green-500 text-xl" />
                ) : (
                  <FaExclamationTriangle className="text-red-500 text-xl" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className={`text-sm font-semibold ${
                    uploadResult.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {uploadResult.message || (uploadResult.success ? 'Validation successful!' : 'Validation failed')}
                  </p>
                  
                  {/* Preview Button (only when successful) */}
                  {uploadResult.success && uploadResult.data && uploadResult.data.length > 0 && (
                    <button
                      onClick={handlePreview}
                      className="px-4 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 text-sm font-medium"
                    >
                      <FaEye />
                      Preview ({uploadResult.data.length} classes)
                      <FaArrowRight size={12} />
                    </button>
                  )}
                </div>
                
                {/* Summary Stats */}
                {uploadResult.summary && (
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="bg-white rounded px-3 py-1.5 border border-gray-200 text-center">
                      <span className="text-xs text-gray-500">Total Rows</span>
                      <p className="text-sm font-semibold text-gray-800">{uploadResult.summary.totalRows || 0}</p>
                    </div>
                    <div className="bg-white rounded px-3 py-1.5 border border-green-200 text-center">
                      <span className="text-xs text-green-600">Valid</span>
                      <p className="text-sm font-semibold text-green-700">{uploadResult.summary.validRows || 0}</p>
                    </div>
                    <div className="bg-white rounded px-3 py-1.5 border border-red-200 text-center">
                      <span className="text-xs text-red-600">Errors</span>
                      <p className="text-sm font-semibold text-red-700">{uploadResult.summary.errorRows || 0}</p>
                    </div>
                    <div className="bg-white rounded px-3 py-1.5 border border-yellow-200 text-center">
                      <span className="text-xs text-yellow-600">Warnings</span>
                      <p className="text-sm font-semibold text-yellow-700">{uploadResult.summary.warningRows || 0}</p>
                    </div>
                  </div>
                )}

                {/* Errors List */}
                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <div className="mt-3 p-3 bg-red-100/50 border border-red-200 rounded-lg text-xs text-red-700 max-h-32 overflow-y-auto">
                    <p className="font-medium mb-1">Errors:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {uploadResult.errors.slice(0, 5).map((err, idx) => (
                        <li key={idx}>
                          Row {err.rowNumber}: {err.errors?.join(', ') || err.error || 'Invalid data'}
                        </li>
                      ))}
                      {uploadResult.errors.length > 5 && (
                        <li className="text-gray-500">... and {uploadResult.errors.length - 5} more errors</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>
            💡 Tip: Download the template first, fill it with your timetable data, then upload.
          </span>
          <span>
            Supports .xlsx, .xls • Max 5MB
          </span>
        </div>
      </div>
    </div>
  );
};

export default ExcelBulkUpload;